import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { patchDeliverableChangeOrder } from '@/api/Sherpa/ChangeOrder';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { DATE_FORMAT } from '@/constants';
import { useUploadDocuments } from '@/features/ChangeOrder/hooks/useUploadDocuments';
import DeliverableHookForm from '@/features/ChangeOrder/Summary/ChangeOrderDeliverables/DeliverableForm';
import { useAppDispatch } from '@/store';
import { notifyUser } from '@/store/Toast';
import { getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import { type DeliverableChangeOrderSchema, validationSchema } from '../DeliverableForm/validationSchema';

type EditDeliverableModalProps = {
  projectId: string;
  changeOrderId: string;
  defaultValues: Partial<DeliverableChangeOrderSchema>;
  onSubmitted?: () => void;
};

export const EditDeliverableModal = NiceModal.create<EditDeliverableModalProps>(
  ({ onSubmitted, changeOrderId, defaultValues, projectId }) => {
    const modal = useModal();
    const dispatch = useAppDispatch();

    const onSuccess = () => {
      onSubmitted?.();
      modal.hide();
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Deliverable change order updated successfully',
          toastType: 'success',
        })
      );
    };

    const { uploadDocuments, isLoading } = useUploadDocuments({
      onUploadReady: ({ documents, params: { changeOrderId, prevDocuments = [], ...restParams } }) =>
        patchDeliverableChangeOrder({
          ...restParams,
          changeOrderId,
          documents: [...prevDocuments, ...documents],
        }),
      onSuccess,
      resourceType: 'changeOrderDeliverable',
    });

    const editMutation = useMutation({
      mutationFn: patchDeliverableChangeOrder,
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    });

    const validateFiles = (templateDocuments: any[]) => {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      templateDocuments?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );

        return null;
      }

      return validFiles;
    };

    const onSubmit = async (formData: DeliverableChangeOrderSchema) => {
      const prevDocuments = formData.recentFiles?.map(({ size, name, key, contentType }) => ({
        size,
        name,
        key,
        contentType,
      }));

      if (formData.templateDocuments?.length) {
        const validFiles = validateFiles(formData.templateDocuments);

        if (validFiles && validFiles.length > 0) {
          uploadDocuments({
            files: formData.templateDocuments,
            params: {
              changeOrderId,
              prevDocuments,
              reason: formData.reason,
              notes: formData.notes,
              consequences: formData.noResponseConsequence,
              proposedDue: dayjs(formData.proposedDueDate).format(DATE_FORMAT),
              responseDueBy: dayjs(formData.responseDueBy).format(DATE_FORMAT),
            },
          });
        }
      } else {
        await editMutation.mutateAsync({
          changeOrderId: Number(changeOrderId),
          reason: formData.reason,
          notes: formData.notes,
          consequences: formData.noResponseConsequence,
          proposedDue: dayjs(formData.proposedDueDate).format(DATE_FORMAT),
          responseDueBy: dayjs(formData.responseDueBy).format(DATE_FORMAT),
          documents: prevDocuments,
        });
        onSuccess();
      }
    };

    if (!modal.visible) return null;

    return (
      <Modal customStyle='max-w-xl p-0'>
        <HookFormProvider defaultValues={defaultValues} resolver={yupResolver(validationSchema) as any}>
          <DeliverableHookForm
            title='Edit Deliverable Change Order'
            hide={modal.hide}
            projectId={projectId}
            onSubmit={onSubmit}
            isLoading={editMutation.isLoading || isLoading}
            disableDeliverable
          />
        </HookFormProvider>
      </Modal>
    );
  }
);
